import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {BaseComponent} from '../../base.component';
import {RouteUtil} from '../../../util/route.util';
import {Params} from '@angular/router';
import {Config} from '../../../common/config';
import {StringUtil} from '../../../util/string.util';
import {DeviceService} from '../../../util/service/device.service';
import {isPlatformBrowser} from '@angular/common';

@Component({
  selector: 'app-landing',
  standalone: false,
  templateUrl: './landing.component.html',
  styleUrl: './landing.component.scss'
})
export class LandingComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('videoElement')
  video!: ElementRef<HTMLVideoElement>;
  isMobile?: boolean;
  businessId?: any;
  videoIds = Config.branch.Details?.Videos ?? [];
  randomVideoUrl = '';
  galleryImages: string[] = [];
  currentImageIndex = 0;
  totalImages = 5;
  autoPlayInterval?: any;
  isAutoPlaying = true;
  isChanging = false;
  protected readonly Config = Config;
  protected readonly StringUtil = StringUtil;

  constructor(injector: Injector, deviceService: DeviceService) {
    super(injector);
    this.isMobile = deviceService.isMobile();
    this.activatedRoute.params.subscribe((params: Params) => {
      if (params['BusinessId'] && params['BusinessId'] !== '') {
        this.businessId = params['BusinessId'];
      }
    });
  }

  get backgroundImage(): string {
    const imageId = Config.branch.Details?.Landing?.ServiceTimeSheet?.Background;
    const url = this.formatImage('', imageId);
    return `url('${url}')`;
  }

  get sortedSocialMedias(): any[] {
    if (!Config.branch?.Details?.SocialMedias) return [];
    const socialMedias = [...Config.branch.Details.SocialMedias];
    return socialMedias.sort((a, b) => {
      if (a.Type === 'WHATSAPP') return -1;
      if (b.Type === 'WHATSAPP') return 1;
      return 0;
    });
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardNavigation(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft') {
      this.previousImage();
    } else if (event.key === 'ArrowRight') {
      this.nextImage();
    }
  }

  override ngOnInit() {
    super.ngOnInit();
    this.setRandomVideo();
    this.initializeGallery();
    this.startAutoPlay();
  }

  ngOnDestroy() {
    this.stopAutoPlay();
  }

  initializeGallery() {
    this.galleryImages = [
      this.formatImage('xxx', Config.branch.Details?.Landing?.Gallary?.Image_1),
      this.formatImage('xxx', Config.branch.Details?.Landing?.Gallary?.Image_2),
      this.formatImage('xxx', Config.branch.Details?.Landing?.Gallary?.Image_3),
      this.formatImage('xxx', Config.branch.Details?.Landing?.Gallary?.Image_4),
      this.formatImage('xxx', Config.branch.Details?.Landing?.Gallary?.Image_5)
    ];
  }

  getGalleryImage(index: number): string {
    const actualIndex = (this.currentImageIndex + index) % this.totalImages;
    return this.galleryImages[actualIndex] || '';
  }

  nextImage() {
    if (this.isChanging) return;
    this.isChanging = true;
    this.currentImageIndex = (this.currentImageIndex + 1) % this.totalImages;
    setTimeout(() => {
      this.isChanging = false;
    }, 500);
  }

  previousImage() {
    if (this.isChanging) return;
    this.isChanging = true;
    this.currentImageIndex = (this.currentImageIndex - 1 + this.totalImages) % this.totalImages;
    setTimeout(() => {
      this.isChanging = false;
    }, 500);
  }

  startAutoPlay() {
    if (isPlatformBrowser(Config.platformId)) {
      this.isAutoPlaying = true;
      this.autoPlayInterval = setInterval(() => {
        if (!this.isChanging) {
          this.nextImage();
        }
      }, 6000);
    }
  }

  stopAutoPlay() {
    this.isAutoPlaying = false;
    if (this.autoPlayInterval) {
      clearInterval(this.autoPlayInterval);
      this.autoPlayInterval = undefined;
    }
  }

  setRandomVideo() {
    this.randomVideoUrl = this.getRandomVideoUrl();
    if (isPlatformBrowser(Config.platformId)) {
      this.playVideoWithRetry();
    }
  }

  playVideoWithRetry(retries = 10, delay = 200) {
    if (retries <= 0) {
      return;
    }
    setTimeout(() => {
      if (this.video?.nativeElement) {
        const videoElement = this.video.nativeElement;
        videoElement.muted = true;
        const playPromise = videoElement.play();
        if (playPromise !== undefined) {
          playPromise.catch(() => {
            this.playVideoWithRetry(retries - 1, delay * 2);
          });
        } else {
          this.playVideoWithRetry(retries - 1, delay * 2);
        }
      } else {
        this.playVideoWithRetry(retries - 1, delay * 2);
      }
    }, delay);
  }

  getRandomVideoUrl(): string {
    if (!this.videoIds?.length) return '';
    const randomIndex = Math.floor(Math.random() * this.videoIds.length);
    const randomId = this.videoIds[randomIndex];
    return this.formatVideo(randomId);
  }

  override loadOnline() {
    super.loadOnline();
  }

  openHomeClicked() {
    if (Config.branch.UniqueIdentifier && Config.branch.UniqueIdentifier !== '') {
      this.router.navigate([RouteUtil.CLIENT_MODULE, RouteUtil.MENU_COMPONENT, Config.branch.UniqueIdentifier]).then();
    } else {
      this.router.navigate([RouteUtil.CLIENT_MODULE]).then();
    }
  }

  openLogin() {
    this.router.navigate([RouteUtil.COMMON_MODULE, RouteUtil.SIGN_IN_COMPONENT]).then();
  }

  onSocialMedialClicked(social?: any) {
    if (social && social.URI) {
      if (social.Type === 'WHATSAPP') {
        const phoneNumber = social.URI.replace(/[^\d]/g, '');
        const whatsappUrl = `https://wa.me/${phoneNumber}`;
        window.open(whatsappUrl, '_blank');
      } else {
        window.open(social.URI, '_blank');
      }
    } else {
      if (Config.branch?.Details?.SocialMedias) {
        const whatsappSocial = Config.branch?.Details?.SocialMedias?.find((s: any) => s.Type === 'WHATSAPP');
        if (whatsappSocial && whatsappSocial.URI) {
          const phoneNumber = whatsappSocial.URI.replace(/[^\d]/g, '');
          const whatsappUrl = `https://wa.me/${phoneNumber}`;
          window.open(whatsappUrl, '_blank');
        }
      }
    }
  }

  onEventClicked(event: any) {
    this.serviceApi.download(event?.FileId).subscribe((res: any) => {
      const url = window.URL.createObjectURL(res.body);
      const newTab = window.open();
      if (newTab) {
        newTab.location.href = url;
      } else {
        window.location.href = url;
      }
    });
  }

  formatTimeSheetText(text: string): string {
    if (!text) return '';
    return text.replace(/<\/?br\s*\/?>/gi, ' ');
  }

  makePhoneCall(phoneNumber: string) {
    if (phoneNumber) {
      const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
      const telUrl = `tel:${cleanNumber}`;
      window.location.href = telUrl;
    }
  }

  openWhatsApp(phoneNumber: string) {
    if (phoneNumber) {
      const cleanNumber = phoneNumber.replace(/[^\d]/g, '');
      const whatsappUrl = `https://wa.me/${cleanNumber}`;
      window.open(whatsappUrl, '_blank');
    }
  }
}
